/**
 * Execute tab content for voice agent: Test Agent options (Inbound, Outbound, Webcall).
 * Renders the same content as TestAgent modal inline.
 * Do not modify TestAgent.tsx; this is a separate component.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Select, SelectItem } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import {
  useGetInboundAttachListQuery,
  usePostOutboundCallMutation,
  usePostWebRtcSessionMutation,
  usePostOfferMutation,
  useTypedSelector,
} from '@circuitry-ai/doc-data';
import { CtyButton, checkType, CtyShowToast, cleanupVoiceAgentWebRtc, startVoiceAgentWebRtcSession } from '@circuitry-ai/doc-ui';

export type ExecutePanelMode = 'inbound' | 'outbound' | 'webcall';

const COUNTRY_CODES = [
  { value: '+91', label: '+91' },
  { value: '+1', label: '+1' },
] as const;

export interface VoiceAgentExecutePanelProps {
  assistantId: string;
  assistantName?: string | null;
}

export const VoiceAgentExecutePanel: React.FC<VoiceAgentExecutePanelProps> = ({
  assistantId,
  assistantName,
}) => {
  const { t } = useTranslation();
  const [testMode, setTestMode] = useState<ExecutePanelMode>('inbound');
  const [countryCode, setCountryCode] = useState('+91');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isCalling, setIsCalling] = useState(false);
  const [webRtcCalling, setWebRtcCalling] = useState(false);
  const [webRtcConnected, setWebRtcConnected] = useState(false);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);

  const token = useTypedSelector((s) => s.auth?.token ?? null);

  const { data: inboundList } = useGetInboundAttachListQuery(assistantId, {
    skip: !assistantId,
    refetchOnMountOrArgChange: true,
  });
  const [postOutboundCall, { isLoading: isOutboundLoading }] = usePostOutboundCallMutation();
  const [postWebRtcSession] = usePostWebRtcSessionMutation();
  const [postOffer] = usePostOfferMutation();

  const inboundNumbers = React.useMemo(() => {
    const numbers = inboundList?.numbers ?? [];
    return numbers
      .filter((n) => n.assistant_id === assistantId || !n.assistant_id)
      .map((n) => n.phone_number ?? n.number ?? '')
      .filter(Boolean);
  }, [inboundList?.numbers, assistantId]);

  const hasInboundNumbers = inboundNumbers.length > 0;
  const isValidPhone = phoneNumber.replace(/\D/g, '').length === 10;

  const handlePhoneChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    setPhoneNumber(digits);
  };

  const handleOutboundCall = useCallback(async () => {
    if (!assistantId || !isValidPhone || isCalling) return;
    const fullNumber = `${countryCode}${phoneNumber.replace(/\D/g, '').trim()}`;
    setIsCalling(true);
    try {
      await postOutboundCall({
        assistant_id: assistantId,
        phonenumber: fullNumber,
        customer_details: { extra: {} },
      }).unwrap();
      CtyShowToast(checkType.success, t('Calling… The agent is calling you now.'));
    } catch (err: unknown) {
      const detail = (err as { data?: { detail?: string } })?.data?.detail;
      const message = detail ?? (err as Error)?.message ?? t('Failed to initiate call.');
      CtyShowToast(checkType.error, message);
    } finally {
      setIsCalling(false);
    }
  }, [assistantId, countryCode, phoneNumber, isValidPhone, isCalling, postOutboundCall, t]);

  const endWebcall = useCallback(() => {
    cleanupVoiceAgentWebRtc(
      { peerConnectionRef, audioRef, localStreamRef },
      () => {
        setWebRtcCalling(false);
        setWebRtcConnected(false);
      }
    );
  }, []);

  const handleStartWebcall = useCallback(async () => {
    if (!assistantId || !token) {
      CtyShowToast(checkType.error, t('Authentication required. Please log in.'));
      return;
    }
    setWebRtcCalling(true);
    setWebRtcConnected(false);
    const refs = { peerConnectionRef, audioRef, localStreamRef };
    await startVoiceAgentWebRtcSession({
      assistantId,
      token,
      t,
      postWebRtcSession,
      postOffer,
      refs,
      logContext: '[VoiceAgentExecutePanel]',
      onConnected: () => {
        setWebRtcCalling(true);
        setWebRtcConnected(true);
      },
      onDisconnected: () => {
        setWebRtcCalling(false);
        setWebRtcConnected(false);
      },
      onRemoteTrackUnmute: () => {
        CtyShowToast(checkType.success, t('Agent is ready. You should hear the agent now.'));
      },
    });
  }, [assistantId, token, postWebRtcSession, postOffer, t]);

  useEffect(() => {
    return () => {
      endWebcall();
    };
  }, [endWebcall]);

  const modeCards: { mode: ExecutePanelMode; icon: string; title: string; subtitle: string }[] = [
    { mode: 'inbound', icon: 'mdi:phone', title: t('Inbound'), subtitle: t('You call the agent') },
    { mode: 'outbound', icon: 'mdi:arrow-left', title: t('Outbound'), subtitle: t('Agent calls you') },
    { mode: 'webcall', icon: 'mdi:web', title: t('Webcall'), subtitle: t('Browser voice call') },
  ];

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="mb-4">
        <p className="text-sm text-default-500 mt-0.5">
          {t('Choose how you want to interact with the assistant.')}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        {modeCards.map(({ mode, icon, title, subtitle }) => (
          <button
            key={mode}
            type="button"
            onClick={() => setTestMode(mode)}
            className={`
              flex flex-col items-center justify-center p-5 rounded-lg border-2 cursor-pointer transition-all text-center space-y-2 min-h-[100px]
              ${testMode === mode
                ? 'border-primary bg-primary/10 shadow-sm'
                : 'border-default-300 hover:bg-default-50'}
            `}
          >
            <Icon icon={icon} className="w-6 h-6 text-primary" />
            <span className="font-medium text-default-900">{title}</span>
            <span className="text-xs text-default-500">{subtitle}</span>
          </button>
        ))}
      </div>

      <div className="p-4 sm:p-6 bg-default-50 rounded-lg space-y-4">
        {testMode === 'inbound' && (
          <>
            <p className="text-sm font-medium text-default-700">{t('Call the agent at:')}</p>
            {hasInboundNumbers ? (
              <div className="space-y-1">
                {inboundNumbers.map((num) => (
                  <p key={num} className="text-sm text-primary tracking-widest font-mono">
                    {num}
                  </p>
                ))}
                <p className="text-xs text-default-500 mt-3">
                  {t('These are the numbers assigned to this agent.')}
                </p>
              </div>
            ) : (
              <p className="text-sm text-default-500">
                {t('No inbound number configured, using default number. Assign an agent\'s phone number when creating or editing this agent to enable inbound testing.')}
              </p>
            )}
            <CtyButton
              color="primary"
              className="w-full sm:w-auto"
              onPress={() => setTestMode('webcall')}
              startContent={<Icon icon="mdi:web" className="w-4 h-4" />}
            >
              {t('Webcall')}
            </CtyButton>
          </>
        )}

        {testMode === 'outbound' && (
          <>
            <div className="grid grid-cols-[auto_1fr] gap-2 items-end">
              <Select
                label={t('Country')}
                selectedKeys={[countryCode]}
                onSelectionChange={(keys) => {
                  const k = Array.from(keys)[0] as string;
                  if (k) setCountryCode(k);
                }}
                classNames={{ trigger: 'w-24 min-w-24' }}
                variant="bordered"
              >
                {COUNTRY_CODES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </Select>
              <Input
                label={t('Phone Number')}
                placeholder="1234567890"
                value={phoneNumber}
                onValueChange={handlePhoneChange}
                maxLength={10}
                inputMode="numeric"
                variant="bordered"
              />
            </div>
            {isCalling && (
              <p className="flex items-center justify-center gap-2 text-success text-sm">
                <Icon icon="mdi:phone-in-talk" className="w-4 h-4 animate-pulse" />
                {t('Call in progress – waiting...')}
              </p>
            )}
            <div className="flex flex-wrap gap-3">
              <CtyButton
                color="primary"
                onPress={handleOutboundCall}
                isDisabled={!isValidPhone || isCalling || isOutboundLoading}
                isLoading={isOutboundLoading}
                startContent={
                  isOutboundLoading ? (
                    <Icon icon="mdi:loading" className="w-4 h-4 animate-spin" />
                  ) : (
                    <Icon icon="mdi:phone" className="w-4 h-4" />
                  )
                }
              >
                {isCalling || isOutboundLoading ? t('Calling...') : t('Call this number')}
              </CtyButton>
              <CtyButton
                color="primary"
                variant="flat"
                onPress={() => setTestMode('webcall')}
                startContent={<Icon icon="mdi:web" className="w-4 h-4" />}
              >
                {t('Webcall')}
              </CtyButton>
            </div>
          </>
        )}

        {testMode === 'webcall' && (
          <>
            <audio ref={audioRef} autoPlay className="hidden">
              <track kind="captions" />
            </audio>
            <div className="flex flex-col items-center justify-center py-6">
              <div
                className="w-64 h-64 rounded-full flex items-center justify-center relative"
                style={{
                  boxShadow: webRtcCalling
                    ? '0 0 0 0 rgba(var(--nextui-colors-primary), 0.7)'
                    : undefined,
                  animation: webRtcCalling ? 'pulse-ring 2s infinite' : undefined,
                }}
              >
                <CtyButton
                  color="primary"
                  size="lg"
                  onPress={handleStartWebcall}
                  isDisabled={webRtcCalling}
                  className="rounded-full w-24 h-24 min-w-24"
                  startContent={
                    webRtcCalling ? (
                      <Icon icon="mdi:loading" className="w-8 h-8 animate-spin" />
                    ) : (
                      <Icon icon="mdi:phone" className="w-8 h-8" />
                    )
                  }
                >
                  {''}
                </CtyButton>
              </div>
              <p className="mt-4 text-sm text-default-500">
                {webRtcCalling ? t('Call in Progress') : t('Talk to Agent')}
              </p>
              {webRtcCalling && (
                <p className="mt-2 text-xs text-default-400">
                  {webRtcConnected
                    ? t('Connected. Speak to the agent.')
                    : t('Connecting... Please wait.')}
                </p>
              )}
              <CtyButton
                color="primary"
                variant="flat"
                className="mt-6"
                onPress={() => endWebcall()}
                isDisabled={!webRtcCalling}
                startContent={<Icon icon="mdi:phone-hangup" className="w-4 h-4" />}
              >
                {t('End Call')}
              </CtyButton>
            </div>
            <style>{`
              @keyframes pulse-ring {
                0% { box-shadow: 0 0 0 0 rgba(var(--nextui-colors-primary), 0.7); }
                50% { box-shadow: 0 0 0 20px rgba(var(--nextui-colors-primary), 0); }
                100% { box-shadow: 0 0 0 0 rgba(var(--nextui-colors-primary), 0); }
              }
            `}</style>
          </>
        )}

        </div>
    </div>
  );
};

export default VoiceAgentExecutePanel;
